import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from './AuthContext';
import { CheckCircle, WarningCircle, SignOut } from '@phosphor-icons/react';

// Synchronously processed component: exchanges the URL-fragment session_id for a JWT cookie.
// Called from the router root whenever `location.hash` contains `session_id=`.
//
// Critical UX gate: after the cookie is set, we DO NOT auto-navigate to "/" — instead
// we show the user *which account Google actually returned* and require a click to
// continue. This stops the silent "wrong Google account auto-selected" trap where a
// stale Chrome Google session logs the user in as someone they didn't intend.
const AuthCallback = () => {
  const { loginWithGoogleSession, logout } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [signedInAs, setSignedInAs] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    // Clear the hash so refreshing the page doesn't re-trigger.
    window.history.replaceState(null, '', window.location.pathname);

    loginWithGoogleSession(sessionId)
      .then((user) => {
        setSignedInAs(user);
      })
      .catch((err) => {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
  }, [loginWithGoogleSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6 text-text-primary">
        <div className="max-w-md bg-surface border border-danger/30 rounded-xl p-6 text-center" data-testid="google-callback-error">
          <WarningCircle size={36} weight="duotone" className="text-danger mx-auto mb-2" />
          <p className="text-danger font-semibold mb-2">Google sign-in failed</p>
          <p className="text-sm text-text-secondary mb-4">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-4 py-2 bg-gold text-black font-bold rounded-lg"
            data-testid="google-callback-back"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (signedInAs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6 text-text-primary">
        <div className="max-w-md w-full bg-surface border border-border-color rounded-xl p-7" data-testid="google-callback-confirm">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={28} weight="duotone" className="text-success" />
            <h2 className="text-xl font-bold font-heading text-gold">Signed in via Google</h2>
          </div>
          <p className="text-sm text-text-secondary mb-4">
            Please confirm this is the account you wanted to use:
          </p>
          <div className="bg-black/40 border border-border-color rounded-lg p-4 mb-5" data-testid="google-callback-identity">
            <p className="text-base font-semibold text-text-primary" data-testid="google-callback-name">{signedInAs.name}</p>
            <p className="text-sm text-text-secondary break-all" data-testid="google-callback-email">{signedInAs.email}</p>
            <p className="text-[10px] uppercase tracking-wider text-gold mt-2" data-testid="google-callback-role">{signedInAs.role}</p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate('/', { replace: true })}
              data-testid="google-callback-continue"
              className="w-full px-4 py-2.5 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
            >
              Continue as {signedInAs.name?.split(' ')[0] || 'this user'}
            </button>
            <button
              onClick={async () => {
                await logout();
                // Clear ALL Google-related cookies/sessions on the Emergent auth gateway by
                // sending the user back to /login. They'll need to pick a different Google
                // account from Google's own chooser.
                navigate('/login', { replace: true });
              }}
              data-testid="google-callback-switch"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
            >
              <SignOut size={16} weight="bold" />
              Not me — sign out & switch account
            </button>
          </div>
          <p className="mt-5 text-[11px] text-text-secondary text-center">
            If Google keeps picking the wrong account, sign out of the other Google account in your browser first, or use an incognito window.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6 text-text-primary">
      <p className="text-text-secondary">Completing sign-in…</p>
    </div>
  );
};

export default AuthCallback;
